import React, { useEffect, useRef, useState } from "react";
import _ from "lodash";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { pageURLs } from "@/constants";
import { hasBlsTenantContext } from "@/helpers/tenant";
import {
  getPendingImports,
  getPendingImport,
  dismissPendingImport,
} from "@/services/v2/pendingImportsService";
import {
  pendingImportChips,
  pendingImportLocationSummary,
} from "@/helpers/pendingImportSummary";

// Pending imports: saved searches an agent's MLS (currently MIBOR/BLC via
// connectMLS) forwarded us. Never auto-created as a real draft — the agent
// must explicitly start one, which prefills the create form the same way an
// existing draft's "resume" click does. Shown above the empty create form.
const PendingImportCards = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const pendingImportsQuery = useQuery({
    queryKey: ["pendingImports"],
    queryFn: async () => {
      const response = await getPendingImports();
      return _.get(response, "data", []);
    },
    // The endpoint 400s without a BLS tenant context (see apiService's
    // x-bls-env header), so on the default Gitcha site it can only fail —
    // twice, with the app's retry — on every page load.
    enabled: hasBlsTenantContext(),
  });
  const pendingImports = pendingImportsQuery.data || [];
  const [pendingImportError, setPendingImportError] = useState(null);
  // One click on Dismiss is permanent (there is no restore endpoint), so the
  // card asks first. Only one card can be asking at a time.
  const [confirmingDismissId, setConfirmingDismissId] = useState(null);
  // The button that had focus unmounts when the confirm row replaces it, so
  // focus moves to Keep, the safe choice, instead of dropping to <body>.
  const keepButtonRef = useRef(null);
  useEffect(() => {
    if (confirmingDismissId != null) keepButtonRef.current?.focus();
  }, [confirmingDismissId]);

  // "Start from this search" only previews the import (read-only GET) — it
  // must not claim it server-side. Claiming happens from AgentWantAdForm on
  // the save that actually creates the want-ad. Claiming here instead would
  // let an agent who clicks Start and then leaves without editing anything
  // (the form only autosaves after a field edit) lose the import for good:
  // it drops off this list once claimed, and a claimed row is excluded from
  // the MLS re-sync that would otherwise bring it back.
  const startPendingImportMutation = useMutation({
    mutationFn: (id) => getPendingImport(id),
    onSuccess: (response) => {
      const prefill = _.get(response, "data", null);
      if (!prefill) {
        setPendingImportError(
          "Something went wrong starting this search. Please try again.",
        );
        return;
      }
      setPendingImportError(null);
      // AgentCreateWantAd.js reads isEdit off location.state.data (not off
      // location.state itself) — must be set here, not as a sibling key. This
      // is the route the form already lives on, so AgentCreateWantAd remounts
      // the form for each new pendingImportId to re-seed it from the new state.
      navigate(pageURLs.agentCreatewantad, {
        state: { data: { ...prefill, isEdit: false } },
      });
    },
    onError: (err) => {
      console.log("Error", err);
      // 404: the search left MIBOR's feed (opt-out / delete) after this list
      // was loaded. Retrying can never work, so drop the card now instead of
      // leaving it for the 5-minute staleTime.
      if (_.get(err, "response.status") === 404) {
        setPendingImportError(
          "This saved search is no longer available in your MLS.",
        );
        queryClient.invalidateQueries({ queryKey: ["pendingImports"] });
        return;
      }
      setPendingImportError(
        "Something went wrong starting this search. Please try again.",
      );
    },
  });

  const dismissPendingImportMutation = useMutation({
    mutationFn: (id) => dismissPendingImport(id),
    onSuccess: () => {
      setPendingImportError(null);
      setConfirmingDismissId(null);
      queryClient.invalidateQueries({ queryKey: ["pendingImports"] });
    },
    onError: (err) => {
      console.log("Error", err);
      setConfirmingDismissId(null);
      setPendingImportError(
        "Something went wrong dismissing this search. Please try again.",
      );
    },
  });

  return (
    <>
      {pendingImports.length > 0 && (
        <>
          <section className="pending-import-section">
            <h2 className="pending-import-title mt-3">
              Start from a saved search
            </h2>
            <p className="pending-import-intro">
              Your MLS shared these with us. Pick one and we'll fill in the
              listing for you. Anything the search doesn't say is filled in for
              you to check.
            </p>
            {pendingImportError && (
              <p className="text-danger">{pendingImportError}</p>
            )}
            {pendingImports.map((pendingImport) => {
              const locationSummary =
                pendingImportLocationSummary(pendingImport);
              const title = locationSummary || "Saved search";
              const confirming = confirmingDismissId === pendingImport.id;
              return (
                <div
                  key={pendingImport.id}
                  className="pending-import-card mt-3"
                >
                  <h3 className="pending-import-card__title">{title}</h3>
                  <ul className="pending-import-card__chips">
                    {pendingImportChips(pendingImport).map((chip) => (
                      <li key={chip}>{chip}</li>
                    ))}
                  </ul>
                  <div className="pending-import-card__actions">
                    {confirming ? (
                      <>
                        <span className="pending-import-card__confirm">
                          Dismiss this search for good?
                        </span>
                        <button
                          type="button"
                          className="btn btn-outline-danger"
                          disabled={dismissPendingImportMutation.isPending}
                          onClick={() =>
                            dismissPendingImportMutation.mutate(
                              pendingImport.id,
                            )
                          }
                        >
                          Yes, dismiss
                        </button>
                        <button
                          type="button"
                          className="btn btn-link"
                          ref={keepButtonRef}
                          onClick={() => setConfirmingDismissId(null)}
                        >
                          Keep
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          type="button"
                          className="btn btn-primary"
                          aria-label={`Start listing from ${title}`}
                          disabled={startPendingImportMutation.isPending}
                          onClick={() =>
                            startPendingImportMutation.mutate(pendingImport.id)
                          }
                        >
                          Start listing
                        </button>
                        <button
                          type="button"
                          className="btn btn-link"
                          aria-label={`Dismiss ${title}`}
                          disabled={dismissPendingImportMutation.isPending}
                          onClick={() =>
                            setConfirmingDismissId(pendingImport.id)
                          }
                        >
                          Dismiss
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </section>
        </>
      )}
      {/* The section above hides itself once the list is empty, taking its
          error with it — keep the reason visible (e.g. the last card was just
          withdrawn). */}
      {pendingImports.length === 0 && pendingImportError && (
        <p className="text-danger mt-3">{pendingImportError}</p>
      )}
    </>
  );
};

export default PendingImportCards;
